import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";

import type { RootState } from "../../../store/store";
import { LOGINPAGE } from "../../auth/routes";
import { useGoBack } from "../../../hooks/useGoBack";
import EcoIcon from "../../../components/eco-icon/EcoIcon";
import { SUPERVISOR_EVALUATION_PAGE, SUPERVISOR_HOME_PAGE } from "../routes";
import {
  createFreelancerEvaluationForRegistrationApi,
  EvaluationFormListItem,
  listEvaluationFormsApi,
} from "../supervisorApi";

import "../components/SupFlow.css";

type RouteParams = {
  registrationId?: string;
};

type FormWithQuestions = EvaluationFormListItem & { questions?: unknown[] };

/** Escolha do formulário da avaliação de freelancer (redesign de 15/10/2026). */
const SupervisorPickEvaluationFormPage: React.FC = () => {
  const navigate = useNavigate();
  // Aberta por link direto, volta para a home do supervisor em vez de sair do app.
  const goBack = useGoBack(SUPERVISOR_HOME_PAGE());
  const { registrationId } = useParams<RouteParams>();

  const token = useSelector((s: RootState) => s.auth.accessToken);

  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [forms, setForms] = useState<FormWithQuestions[]>([]);

  const loadForms = useCallback(async () => {
    if (!token) return;

    try {
      setLoading(true);
      const list = await listEvaluationFormsApi({ token, category: "FREELANCER", includeQuestions: true });
      setForms(list as FormWithQuestions[]);
    } catch (err: any) {
      toast.error(err?.message ?? "Erro ao listar formulários.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  const createEvaluation = useCallback(
    async (formId: string) => {
      if (!token) return;
      if (!registrationId) return;

      try {
        setCreating(true);
        const ev = await createFreelancerEvaluationForRegistrationApi({
          token,
          registrationId,
          formId,
        });
        navigate(SUPERVISOR_EVALUATION_PAGE(ev.id), { replace: true });
      } catch (err: any) {
        toast.error(err?.message ?? "Erro ao criar avaliação.");
      } finally {
        setCreating(false);
      }
    },
    [token, registrationId, navigate],
  );

  useEffect(() => {
    if (!token) {
      toast.error("Você precisa estar logado.");
      navigate(LOGINPAGE(), { replace: true });
      return;
    }

    if (!registrationId) {
      toast.error("Parâmetros inválidos (registrationId ausente).");
      goBack();
      return;
    }

    loadForms();
  }, [token, registrationId, navigate, loadForms, goBack]);

  return (
    <div className="supFlow">
      <header className="supFlow__band">
        <div className="supFlow__bandInner">
          <div className="supFlow__bandTop">
            <button type="button" className="supFlow__back" onClick={goBack} aria-label="Voltar" disabled={creating}>
              <EcoIcon name="arrowLeft" size={22} />
            </button>
            <span className="supFlow__topTitle">Avaliar freelancer</span>
            <span className="supFlow__spacer" aria-hidden="true" />
          </div>
        </div>
      </header>

      <main className="supFlow__main">
        <div className="supFlow__intro">
          <h1 className="supFlow__introTitle">Qual formulário?</h1>
          <p className="supFlow__introText">Escolha um. A avaliação abre em seguida.</p>
        </div>

        {loading ? (
          <div className="supFlow__empty">Carregando...</div>
        ) : forms.length === 0 ? (
          <div className="supFlow__empty">Nenhum formulário encontrado.</div>
        ) : (
          forms.map((f) => (
            <button
              key={f.id}
              type="button"
              className="supFlow__link"
              onClick={() => createEvaluation(f.id)}
              disabled={creating}
            >
              <span className="supFlow__linkIcon" aria-hidden="true">
                <EcoIcon name="clipboardCheck" size={22} strokeWidth={1.8} />
              </span>
              <span className="supFlow__optionText">
                <span className="supFlow__optionTitle">{f.title}</span>
                {Array.isArray(f.questions) ? (
                  <span className="supFlow__optionSub">
                    {f.questions.length} {f.questions.length === 1 ? "pergunta" : "perguntas"}
                  </span>
                ) : null}
              </span>
              <EcoIcon name="chevronRight" size={20} />
            </button>
          ))
        )}
      </main>
    </div>
  );
};

export default SupervisorPickEvaluationFormPage;
